import type { Player } from '@ofc/shared';

/**
 * A player's face: their picture if they have uploaded one, otherwise their emoji.
 *
 * Every game shows players in a dozen places — rosters, reveals, leaderboards —
 * so this is the one place that decides what a player looks like.
 */
export function Avatar({
  player,
  size = 22,
}: {
  player: Pick<Player, 'emoji' | 'color' | 'userId' | 'picture' | 'frame'> | undefined;
  size?: number;
}) {
  if (!player) return <span aria-hidden>•</span>;

  const face =
    player.picture && player.userId ? (
      <img
        src={`/api/avatar/${player.userId}`}
        alt=""
        width={size}
        height={size}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          border: `1.5px solid ${player.color}`,
          flexShrink: 0,
          display: 'block',
        }}
      />
    ) : (
      <span aria-hidden style={{ fontSize: size * 0.82, lineHeight: 1 }}>
        {player.emoji}
      </span>
    );

  if (!player.frame || player.frame === 'none') return face;

  // The ring is drawn around the face, in the player's colour, by theme.css.
  return (
    <span
      aria-hidden
      className={`avatar-frame frame-${player.frame}`}
      style={{ ['--who' as string]: player.color, width: size + 8, height: size + 8 }}
    >
      {face}
    </span>
  );
}

/**
 * The backing a player's name sits on.
 *
 * Each plate is built from the player's own neon colour rather than carrying
 * colours of its own, so nothing here can fight the palette — which also means
 * the accessibility settings keep working without knowing plates exist.
 */
export function plateStyle(
  plate: string | undefined,
  colour: string,
): React.CSSProperties | undefined {
  const tint = (pct: number) => `color-mix(in srgb, ${colour} ${pct}%, transparent)`;

  switch (plate) {
    case 'glow':
      return { background: `radial-gradient(120% 140% at 50% 0%, ${tint(28)}, transparent 70%)` };
    case 'stripes':
      return {
        background: `repeating-linear-gradient(135deg, ${tint(16)} 0 6px, transparent 6px 12px)`,
      };
    case 'grid':
      return {
        background: `linear-gradient(${tint(12)} 1px, transparent 1px) 0 0 / 100% 8px,
                     linear-gradient(90deg, ${tint(12)} 1px, transparent 1px) 0 0 / 8px 100%`,
      };
    case 'sunset':
      return { background: `linear-gradient(180deg, ${tint(26)}, transparent 85%)` };
    case 'static':
      return {
        background: `repeating-linear-gradient(0deg, ${tint(14)} 0 1px, transparent 1px 3px)`,
      };
    case 'carbon':
      return {
        background: `repeating-linear-gradient(45deg, ${tint(10)} 0 3px, transparent 3px 6px)`,
      };
    case 'marquee':
      return {
        background: `radial-gradient(circle at 6px 50%, ${tint(45)} 1.5px, transparent 2px) 0 0 / 14px 100%`,
      };
    // ---- earned, and moving ----------------------------------------------
    case 'shimmer':
      return {
        background: `linear-gradient(110deg, transparent 30%, ${tint(34)} 50%, transparent 70%) 0 0 / 250% 100%`,
        animation: 'ofc-slide 3.2s linear infinite',
      };
    case 'pulse':
      return {
        ['--plate' as string]: tint(45),
        background: `radial-gradient(120% 140% at 50% 50%, ${tint(12)}, transparent 75%)`,
        animation: 'ofc-pulse 2.4s ease-in-out infinite',
      };
    case 'scan':
      return {
        background: `repeating-linear-gradient(0deg, ${tint(20)} 0 1px, transparent 1px 4px) 0 0 / 100% 24px`,
        animation: 'ofc-scan 1.6s linear infinite',
      };
    case 'aurora':
      return {
        background: `linear-gradient(120deg, ${tint(30)}, color-mix(in srgb, #22e0ff 18%, transparent), color-mix(in srgb, #c46bff 22%, transparent), ${tint(30)}) 0 0 / 300% 100%`,
        animation: 'ofc-drift 8s ease-in-out infinite',
      };
    case 'inferno':
      return {
        background: `linear-gradient(0deg, color-mix(in srgb, #ff4f1f 42%, transparent), color-mix(in srgb, #ffc247 16%, transparent) 55%, transparent 90%) 50% 100% / 100% 100% no-repeat`,
        animation: 'ofc-burn 1.3s ease-in-out infinite',
      };
    // Gold and Holo ignore the player's colour on purpose: they are prizes, and
    // should look the same on everyone who has won one.
    case 'gold':
      return {
        background:
          'linear-gradient(110deg, #b8860b33 20%, #ffd70066 40%, #fff6c077 50%, #ffd70066 60%, #b8860b33 80%) 0 0 / 250% 100%',
        borderColor: '#ffd700aa',
        animation: 'ofc-slide 3.6s linear infinite',
      };
    case 'holo':
      return {
        background:
          'linear-gradient(115deg, #ff2d9540, #ffc24740, #7cff4f40, #22e0ff40, #c46bff40, #ff2d9540) 0 0 / 300% 100%',
        borderColor: '#ffffff66',
        animation: 'ofc-drift 4s linear infinite',
      };
    // ---- added later --------------------------------------------------------
    case 'dots':
      return { background: `radial-gradient(${tint(30)} 1.5px, transparent 2px) 0 0 / 9px 9px` };
    case 'waves':
      return {
        background: `radial-gradient(circle at 50% 120%, transparent 6px, ${tint(18)} 7px, transparent 8px) 0 0 / 16px 10px`,
      };
    case 'chevron':
      return {
        background: `linear-gradient(135deg, ${tint(18)} 25%, transparent 25%) -6px 0 / 12px 12px,
                     linear-gradient(225deg, ${tint(18)} 25%, transparent 25%) -6px 0 / 12px 12px`,
      };
    case 'checker':
      return {
        background: `conic-gradient(${tint(16)} 25%, transparent 0 50%, ${tint(16)} 0 75%, transparent 0) 0 0 / 14px 14px`,
      };
    case 'lava':
      return {
        background: `radial-gradient(40% 60% at 20% 80%, ${tint(40)}, transparent 70%),
                     radial-gradient(35% 55% at 75% 25%, color-mix(in srgb, #ff7a1a 30%, transparent), transparent 70%)`,
        backgroundSize: '200% 200%',
        animation: 'ofc-drift 6s ease-in-out infinite',
      };
    case 'matrix':
      return {
        background: `repeating-linear-gradient(90deg, color-mix(in srgb, #00ff66 22%, transparent) 0 1px, transparent 1px 9px) 0 0 / 100% 24px,
                     repeating-linear-gradient(0deg, color-mix(in srgb, #00ff66 10%, transparent) 0 2px, transparent 2px 6px)`,
        animation: 'ofc-scan 1.2s linear infinite',
      };
    case 'galaxy':
      return {
        background: `radial-gradient(1px 1px at 20% 30%, #fff, transparent),
                     radial-gradient(1px 1px at 70% 60%, #fff, transparent),
                     radial-gradient(1.5px 1.5px at 45% 75%, #fff, transparent),
                     radial-gradient(1px 1px at 85% 20%, #fff, transparent),
                     linear-gradient(120deg, color-mix(in srgb, #3b1d6e 60%, transparent), ${tint(28)}, color-mix(in srgb, #0b3a6e 60%, transparent))`,
        backgroundSize: '100% 100%, 100% 100%, 100% 100%, 100% 100%, 300% 100%',
        animation: 'ofc-drift 10s ease-in-out infinite',
      };
    case 'prism':
      return {
        background: 'linear-gradient(90deg, #ff2d9530, #ffc24730, #7cff4f30, #22e0ff30, #c46bff30)',
        borderColor: '#ffffff55',
      };
    case 'camo':
      return {
        background: `radial-gradient(30% 40% at 20% 30%, color-mix(in srgb, #4b5d23 45%, transparent), transparent 70%),
                     radial-gradient(25% 35% at 70% 70%, color-mix(in srgb, #6b8e23 40%, transparent), transparent 70%),
                     radial-gradient(30% 30% at 85% 20%, color-mix(in srgb, #2f3b14 50%, transparent), transparent 70%)`,
      };
    case 'flames':
      return {
        background: `repeating-radial-gradient(circle at 50% 160%, color-mix(in srgb, #ff4f1f 34%, transparent) 0 10px, color-mix(in srgb, #ffc247 18%, transparent) 10px 18px, transparent 18px 26px)`,
        backgroundSize: '100% 140%',
        animation: 'ofc-burn 1.1s ease-in-out infinite',
      };
    // A prize, like Gold and Holo: the same on everyone who has earned it.
    case 'platinum':
      return {
        background:
          'linear-gradient(110deg, #8a8d9133 20%, #e5e4e277 40%, #ffffff99 50%, #e5e4e277 60%, #8a8d9133 80%) 0 0 / 250% 100%',
        borderColor: '#e5e4e2cc',
        boxShadow: '0 0 14px #e5e4e266',
        animation: 'ofc-slide 3s linear infinite',
      };
    default:
      return undefined;
  }
}
